import { jsPDF as JSPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'

import PoppinsRegularURL from '@/assets/fonts/poppins/Poppins-Regular.ttf'
import PoppinsSemiBoldURL from '@/assets/fonts/poppins/Poppins-SemiBold.ttf'
import KompackLogoData from '@/assets/images/kompack/logo.png'

import { IDR } from '@/libs/currency'
import ttdBase64 from './ttdBase64'

let cachedInvoiceKompackAssets = null

/**
 * Mengambil file dan mengubahnya menjadi Base64 string.
 * @param {Blob|File} file - File atau Blob yang akan dikonversi.
 * @returns {Promise<string>} Promise yang menghasilkan Base64 string.
 */
function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

/**
 * Mengambil dan meng-cache asset yang diperlukan untuk invoice Kompack (font, logo, ttd).
 * @returns {Promise<{fontRegular: string, fontSemiBold: string, logo: string, ttd: string}>} Promise yang menghasilkan objek asset.
 */
const getInvoiceAssets = async () => {
  if (cachedInvoiceKompackAssets) {
    return cachedInvoiceKompackAssets
  }

  const [fontRegularResponse, fontBoldResponse] = await Promise.all([
    fetch(PoppinsRegularURL),
    fetch(PoppinsSemiBoldURL),
  ])

  const fontRegularBuffer = await fontRegularResponse.blob()
  const fontBoldBuffer = await fontBoldResponse.blob()

  const fontRegularBase64 = await getBase64(fontRegularBuffer)
  const fontSemiBoldBase64 = await getBase64(fontBoldBuffer)

  cachedInvoiceKompackAssets = {
    fontRegular: fontRegularBase64.split(',')[1],
    fontSemiBold: fontSemiBoldBase64.split(',')[1],
    logo: KompackLogoData,
    ttd: ttdBase64, // using base64 string instead of image file to avoid user reach the image file easily
  }

  return cachedInvoiceKompackAssets
}

/**
 * Membuat dan mengunduh file PDF invoice Kompack dengan data yang diberikan.
 * @param {Object} params - Parameter untuk invoice.
 * @param {Object} params.table - Data tabel produk dan biaya low moving stock.
 * @param {string} params.partnerName - Nama partner yang ditagih.
 * @param {string} params.period - Periode tagihan (misal: 'Januari 2024').
 * @param {string} params.status - Status pembayaran ('PAID' atau lainnya).
 * @returns {Promise<void>} Promise yang selesai saat file PDF telah diunduh.
 */
const generateKompackInvoice = async ({
  table,
  partnerName,
  period,
  status,
}) => {
  try {
    const assets = await getInvoiceAssets()

    const doc = new JSPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
    })

    doc.addFileToVFS('Poppins-Regular.ttf', assets.fontRegular)
    doc.addFont('Poppins-Regular.ttf', 'Poppins', 'normal')
    doc.addFileToVFS('Poppins-SemiBold.ttf', assets.fontSemiBold)
    doc.addFont('Poppins-SemiBold.ttf', 'Poppins', 'semibold')

    const margin = 10
    const pageWidth = doc.internal.pageSize.getWidth()
    const pageHeight = doc.internal.pageSize.getHeight()

    // Header -- Logo Kompack
    doc.addImage(assets.logo, 'PNG', 15, 15, 30, 30)
    doc.rect(10, 10, 40, 40)

    // Header -- Title
    doc.setFont('Poppins', 'semibold')
    doc.setFontSize(24)
    doc.text('INVOICE KOMPACK', 126, 23)
    doc.line(50, 30.5, pageWidth - 10, 30.5)

    // Header -- Footer
    doc.setFontSize(12)
    doc.text('Komerce', 55, 39.5)
    const statusText = status === 'PAID' ? 'LUNAS' : 'BELUM LUNAS'
    const statusTextWidth = doc.getTextWidth(statusText)
    doc.text(statusText, pageWidth - statusTextWidth - 20, 39.5)
    doc.setFont('Poppins', 'normal')
    doc.text('Jl Sawah Tengah No. 1 Tunjungmuli, Karangmoncol, Purbalingga', 55, 45)

    // Content -- Header
    doc.text(`Kepada: ${partnerName}`, 15, 62)
    doc.text(`Dengan ini kami lampirkan Tagihan Kompack Bulan ${period}`, 15, 68)

    // Content -- Tabel
    const tableColumn = ['Gudang', 'Produk', 'Volume', 'Stok Terkena Low Moving', 'Biaya Low Moving Stock (100rb/m³/bulan)']

    const tableRows = []
    table.products.forEach((product, index) => {
      tableRows.push([
        table.warehouse_name || '-',
        `${product.product_name || '-'}${product.variant_name ? ` ${product.variant_name}` : ''}`,
        `${product.volume || '-'} m³`,
        product.low_moving_count || '-',
        `${IDR(product.low_moving_cost || '-')}`,
      ])
    })
    tableRows.push([
      {
        content: 'Total Biaya Low Moving Stock',
        rowSpan: 1,
        colSpan: 4,
        styles: { valign: 'middle', halign: 'right', fillColor: '#E2E2E2' },
      },
      {
        content: `${IDR(table.total_low_moving_cost)}`,
        styles: { valign: 'middle', halign: 'left', fillColor: '#E2E2E2' },
      },
    ])
    autoTable(doc, {
      theme: 'grid',
      margin: {
        top: 10,
        right: 20,
        bottom: 10,
        left: 20,
      },
      tableLineColor: '#333333',
      tableLineStyle: 'solid',
      headStyles: {
        lineWidth: 0.2,
        lineColor: '#333333',
        fillColor: '#E2E2E2',
        textColor: '#333333',
        fontSize: 12,
        fontStyle: 'bold',
        valign: 'middle',
        cellPadding: 3,
      },
      bodyStyles: {
        lineWidth: 0.2,
        lineColor: '#333333',
        textColor: '#333333',
        fontSize: 12,
        fontStyle: 'normal',
        valign: 'middle',
        cellPadding: 3,
      },
      head: [tableColumn],
      body: tableRows,
      startY: 80,
      didDrawPage(data) {
        const { doc: page } = data
        page.setLineWidth(0.2)
        page.setDrawColor('#333333')
        page.rect(margin, margin, pageWidth - margin * 2, pageHeight - margin * 2)
      },
    })

    // TTD
    const lastTableYPosition = doc.lastAutoTable.finalY
    if (pageHeight - lastTableYPosition < 70) {
      doc.addPage()
      doc.rect(10, 10, pageWidth - 20, pageHeight - 20)
      doc.addImage(assets.ttd, 'PNG', pageWidth - 80, 15, 40, 30)
      doc.setFont('Poppins', 'semibold')
      doc.text('Hendrik Kurniawan', pageWidth - (61 + 20.5), 50)
      doc.setFont('Poppins', 'normal')
      doc.text('Head of Operational Business Kompack', pageWidth - 103.5, 55)
    } else {
      doc.addImage(assets.ttd, 'PNG', pageWidth - 80, lastTableYPosition + 10, 40, 30)
      doc.setFont('Poppins', 'semibold')
      doc.text('Hendrik Kurniawan', pageWidth - (61 + 20.5), lastTableYPosition + 45)
      doc.setFont('Poppins', 'normal')
      doc.text('Head of Operational Business Kompack', pageWidth - 103.5, lastTableYPosition + 50)
    }

    doc.save(`invoice-kompack-${partnerName}-${period}-low-moving-stock.pdf`)
  } catch (error) {
    throw new Error(error)
  }
}

export default generateKompackInvoice
